import { useState, useCallback, useRef, useEffect } from 'react';
import { AddressValidationService } from '../services/addressValidationService';
import { Country, State } from '../services/locationService';

export interface PostalCodeValidationResult {
  isValid: boolean;
  message?: string;
  isValidating: boolean;
}

// US ZIP code first digit to state mapping (approximate)
// This covers the main ranges - some border cases exist
const US_ZIP_STATE_MAP: Record<string, string[]> = {
  '0': ['CT', 'MA', 'ME', 'NH', 'NJ', 'NY', 'PR', 'RI', 'VT', 'VI', 'AE', 'AA'],
  '1': ['DE', 'NY', 'PA'],
  '2': ['DC', 'MD', 'NC', 'SC', 'VA', 'WV'],
  '3': ['AL', 'FL', 'GA', 'MS', 'TN', 'AA'],
  '4': ['IN', 'KY', 'MI', 'OH'],
  '5': ['IA', 'MN', 'MT', 'ND', 'SD', 'WI'],
  '6': ['IL', 'KS', 'MO', 'NE'],
  '7': ['AR', 'LA', 'OK', 'TX'],
  '8': ['AZ', 'CO', 'ID', 'NM', 'NV', 'UT', 'WY'],
  '9': ['AK', 'AS', 'CA', 'GU', 'HI', 'MH', 'FM', 'MP', 'OR', 'PW', 'WA', 'AP'],
};

// Common US state codes
const US_STATE_CODES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
  'DC', 'PR', 'VI', 'GU', 'AS', 'MP',
];

interface UsePostalCodeValidationOptions {
  debounceMs?: number;
  validateOnServer?: boolean;
}

/**
 * Hook to validate postal codes against selected state/country.
 * Performs client-side validation for US postal codes and optionally
 * calls the server-side AddressValidationService for additional validation.
 */
export const usePostalCodeValidation = (options: UsePostalCodeValidationOptions = {}) => {
  const { debounceMs = 800, validateOnServer = false } = options;

  const [validationResult, setValidationResult] = useState<PostalCodeValidationResult>({
    isValid: true,
    isValidating: false,
  });

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  /**
   * Validate US postal code against state code
   */
  const validateUSPostalCode = useCallback((
    postalCode: string,
    stateCode: string
  ): { isValid: boolean; message?: string } => {
    // Clean and normalize inputs
    const cleanZip = postalCode.trim().replace(/\D/g, '');
    const cleanState = stateCode.trim().toUpperCase();

    // Basic format validation - must be 5 digits (or 5+4)
    if (cleanZip.length < 5) {
      return {
        isValid: false,
        message: 'US ZIP code must be at least 5 digits',
      };
    }

    const firstDigit = cleanZip.charAt(0);
    const validStatesForDigit = US_ZIP_STATE_MAP[firstDigit];

    if (!validStatesForDigit) {
      return { isValid: true }; // Unknown format, allow it
    }

    // Check if the state is valid for this ZIP prefix
    if (!validStatesForDigit.includes(cleanState)) {
      // Find what states this ZIP would be valid for
      const expectedStates = validStatesForDigit.slice(0, 3).join(', ');
      return {
        isValid: false,
        message: `ZIP code ${postalCode} does not match ${cleanState}. This ZIP prefix is typically for: ${expectedStates}`,
      };
    }

    return { isValid: true };
  }, []);

  /**
   * Main validation function
   */
  const validatePostalCode = useCallback((
    postalCode: string,
    state: State | null,
    country: Country | null,
    allStates: State[] = [],
    allCountries: Country[] = []
  ) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Skip validation if inputs are incomplete
    if (!postalCode || !state || !country) {
      setValidationResult({ isValid: true, isValidating: false });
      return;
    }

    // Start debounced validation
    setValidationResult(prev => ({ ...prev, isValidating: true }));

    debounceTimerRef.current = setTimeout(async () => {
      try {
        // Check if this is a US address
        const isUSAddress = country.iso2?.toUpperCase() === 'US' ||
                           country.name?.toLowerCase() === 'united states';

        if (isUSAddress) {
          // Get state code
          const stateCode = state.state_code ||
                           (state.name && US_STATE_CODES.find(
                             code => state.name.toUpperCase().includes(code)
                           )) || '';

          if (stateCode) {
            const result = validateUSPostalCode(postalCode, stateCode);
            if (!result.isValid) {
              setValidationResult({
                isValid: false,
                message: result.message,
                isValidating: false,
              });
              return;
            }
          }
        }

        // If server validation is enabled, call the API
        if (validateOnServer && postalCode.length >= 3) {
          abortControllerRef.current?.abort();
          abortControllerRef.current = new AbortController();

          try {
            const response = await AddressValidationService.validateAddress({
              street1: '', // Empty - we just want postal validation
              city: '',
              state: state.state_code || state.name || '',
              postalCode: postalCode,
              country: country.iso2 || '',
            });

            // Check if response indicates postal code mismatch
            const hasPostalMismatch =
              response.message?.toLowerCase().includes('postal') ||
              response.message?.toLowerCase().includes('zip') ||
              response.warnings?.some(w =>
                w.toLowerCase().includes('postal') ||
                w.toLowerCase().includes('zip') ||
                w.toLowerCase().includes('mismatch')
              );

            if (!response.valid && hasPostalMismatch) {
              setValidationResult({
                isValid: false,
                message: response.message || 'Postal code does not match the selected state',
                isValidating: false,
              });
              return;
            }
          } catch (error) {
            // Don't block on server validation errors
            console.warn('Server postal validation failed:', error);
          }
        }

        // All validations passed
        setValidationResult({ isValid: true, isValidating: false });
      } catch (error) {
        // Don't block on validation errors
        console.warn('Postal code validation error:', error);
        setValidationResult({ isValid: true, isValidating: false });
      }
    }, debounceMs);
  }, [debounceMs, validateOnServer, validateUSPostalCode]);

  /**
   * Clear validation state
   */
  const clearValidation = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setValidationResult({ isValid: true, isValidating: false });
  }, []);

  return {
    ...validationResult,
    validatePostalCode,
    clearValidation,
  };
};

export default usePostalCodeValidation;
